import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Badge, Alert, Spinner } from "reactstrap";
import { getScreeningReport } from "../../../helpers/checkrApi";
import CreditScoreGauge from "./CreditScoreGauge";

const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
const formatCents = (c) => (c != null ? `$${(c / 100).toLocaleString()}` : "—");
const reportBadgeColor = (status) => (status === "clear" ? "success" : status === "consider" ? "warning" : "secondary");

/**
 * Criminal / credit / eviction / identity-verification screening report for
 * one application. Shared between the Applications page and a property's
 * application detail view.
 * @param {string} [currentStatus] The badge status the parent currently has
 *  stored (backgroundCheck.status) — compared against the live report so a
 *  stale "in_progress" badge can be corrected without a manual click.
 * @param {() => void} [onComplete] Called once if the live report is done
 *  (order.completed_at present) but currentStatus hasn't caught up yet.
 */
const ScreeningReportPanel = ({ applicationId, applicantId, currentStatus, onComplete }) => {
  const [report, setReport] = useState(null); // null = loading, "not_ready", or the report object
  const [error, setError] = useState("");
  const notifiedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    notifiedRef.current = false;
    setReport(null);
    setError("");
    getScreeningReport(applicationId, applicantId)
      .then((r) => { if (!cancelled) setReport(r); })
      .catch((e) => {
        if (cancelled) return;
        if (e.status === 404) setReport("not_ready");
        else setError(e.message || "Couldn't load the screening report.");
      });
    return () => { cancelled = true; };
  }, [applicationId, applicantId]);

  // The report itself is fetched live from Checkr, so it can already show a
  // finished report while our own cached backgroundCheck.status still says
  // "in_progress" — self-heal that mismatch instead of leaving it stuck.
  // notifiedRef caps this to one attempt per load, since onComplete is a new
  // function reference every parent render and would otherwise refire forever.
  useEffect(() => {
    if (report && typeof report === "object" && report.order?.completed_at && currentStatus !== "complete" && !notifiedRef.current) {
      notifiedRef.current = true;
      onComplete?.();
    }
  }, [report, currentStatus, onComplete]);

  if (error) return <Alert color="danger" className="py-2 small mb-0">{error}</Alert>;

  if (report === null) {
    return <div className="text-center py-3"><Spinner size="sm" color="primary" /></div>;
  }

  if (report === "not_ready") {
    return (
      <Alert color="secondary" className="py-2 small mb-0">
        <i className="bx bx-time-five me-1" />
        No report yet — the screening hasn't completed for this applicant.
      </Alert>
    );
  }

  // A missing product could mean it's not in the package, or that Checkr just
  // hasn't finished it yet — only claim "not included" once the order itself is done.
  const emptySectionText = report.order?.completed_at ? "Not included in this package." : "Pending.";

  return (
    <>
      {report.order?.completed_at && (
        <p className="small text-muted mb-2">
          <i className="bx bx-calendar-check me-1" />
          Screening started {formatDate(report.order.created_at)} · completed {formatDate(report.order.completed_at)}
        </p>
      )}
      <Row className="g-3">
      <Col md={4}>
        <div
          className="rounded-3 p-3 h-100 bg-white border"
          style={{ borderLeft: `4px solid ${reportBadgeColor(report.criminal_history?.status) === "success" ? "#34c38f" : reportBadgeColor(report.criminal_history?.status) === "warning" ? "#f1b44c" : "#cbd5e1"}` }}
        >
          <h6 className="fw-semibold mb-3 d-flex align-items-center justify-content-between">
            <span><i className="bx bx-shield-quarter me-1 text-primary" />Criminal History</span>
            {report.criminal_history?.status && (
              <Badge color={reportBadgeColor(report.criminal_history.status)} pill className="fs-6">
                {report.criminal_history.status}
              </Badge>
            )}
          </h6>
          {!report.criminal_history ? (
            <p className="small text-muted mb-0">{emptySectionText}</p>
          ) : report.criminal_history.records.length === 0 ? (
            <p className="small text-muted mb-0"><i className="bx bx-check-circle text-success me-1" />No criminal records found.</p>
          ) : (
            report.criminal_history.records.map((r, i) => (
              <div key={i} className="small border rounded-2 p-2 mb-2" style={{ background: "#fffaf0" }}>
                {(r.charges || []).map((c, j) => (
                  <div key={j} className="fw-medium">{c.charge || "Unspecified charge"} <span className="text-muted">({c.type || "unknown"})</span></div>
                ))}
                <div className="text-muted">{r.court_name}, {r.county} County, {r.state}</div>
                <div className="text-muted">Filed {formatDate(r.file_date)}</div>
              </div>
            ))
          )}
        </div>
      </Col>
      <Col md={4}>
        <div className="rounded-3 p-3 h-100 bg-white border">
          <h6 className="fw-semibold mb-3"><i className="bx bx-credit-card me-1 text-primary" />Credit Report</h6>
          {!report.credit_report ? (
            <p className="small text-muted mb-0">{emptySectionText}</p>
          ) : report.credit_report.credit_file_status !== "available" ? (
            <p className="small text-muted mb-0">
              Credit file {report.credit_report.credit_file_status.replace("_", " ")}.
            </p>
          ) : (
            <>
              <CreditScoreGauge score={report.credit_report.credit_score} />
              <div className="small mt-3 d-flex flex-column gap-1">
                <div className="d-flex justify-content-between"><span className="text-muted">Accounts</span><span>{report.credit_report.credit_summary?.total_accounts ?? "—"}</span></div>
                <div className="d-flex justify-content-between"><span className="text-muted">Avg. Account Age</span><span>{report.credit_report.credit_summary?.average_account_age_months != null ? `${report.credit_report.credit_summary.average_account_age_months} mo` : "—"}</span></div>
                <div className="d-flex justify-content-between"><span className="text-muted">On-time Payments</span><span>{report.credit_report.credit_summary?.on_time_payment_rate != null ? `${Math.round(report.credit_report.credit_summary.on_time_payment_rate * 100)}%` : "—"}</span></div>
                <div className="d-flex justify-content-between"><span className="text-muted">Credit Utilization</span><span>{report.credit_report.credit_summary?.credit_utilization?.rate != null ? `${report.credit_report.credit_summary.credit_utilization.rate}%` : "—"}</span></div>
                <div className="d-flex justify-content-between"><span className="text-muted">Revolving Balance</span><span>{formatCents(report.credit_report.credit_summary?.credit_utilization?.balance_cents)}</span></div>
                <div className="d-flex justify-content-between"><span className="text-muted">Est. Monthly Payment</span><span>{formatCents(report.credit_report.credit_summary?.estimated_monthly_payment_cents)}</span></div>
                <div className="d-flex justify-content-between"><span className="text-muted">Collections</span><span>{report.credit_report.credit_summary?.collections_count ?? "—"}</span></div>
                <div className="d-flex justify-content-between"><span className="text-muted">Charge-offs</span><span>{report.credit_report.credit_summary?.charge_offs_count ?? "—"}</span></div>
                <div className="d-flex justify-content-between"><span className="text-muted">Bankruptcies</span><span>{report.credit_report.credit_summary?.bankruptcies_count ?? "—"}</span></div>
              </div>
            </>
          )}
        </div>
      </Col>
      <Col md={4}>
        <div
          className="rounded-3 p-3 h-100 bg-white border"
          style={{ borderLeft: `4px solid ${report.eviction_history?.records.length ? "#f1b44c" : "#34c38f"}` }}
        >
          <h6 className="fw-semibold mb-3 d-flex align-items-center justify-content-between">
            <span><i className="bx bx-home-smile me-1 text-primary" />Eviction History</span>
            {report.eviction_history && (
              <Badge color={report.eviction_history.records.length ? "warning" : "success"} pill className="fs-6">
                {report.eviction_history.records.length ? "consider" : "clear"}
              </Badge>
            )}
          </h6>
          {!report.eviction_history ? (
            <p className="small text-muted mb-0">{emptySectionText}</p>
          ) : report.eviction_history.records.length === 0 ? (
            <p className="small text-muted mb-0"><i className="bx bx-check-circle text-success me-1" />No eviction filings found.</p>
          ) : (
            report.eviction_history.records.map((r, i) => (
              <div key={i} className="small border rounded-2 p-2 mb-2" style={{ background: "#fffaf0" }}>
                <div className="fw-medium">{r.plaintiff || "Unknown plaintiff"}</div>
                <div className="text-muted">Filed {formatDate(r.file_date)}</div>
                {r.judgment && <div className="text-muted">{r.judgment} · {formatCents(r.judgment_amount_cents)}</div>}
              </div>
            ))
          )}
        </div>
      </Col>
      <Col md={4}>
        <div
          className="rounded-3 p-3 h-100 bg-white border"
          style={{ borderLeft: `4px solid ${reportBadgeColor(report.identity_verification?.status) === "success" ? "#34c38f" : reportBadgeColor(report.identity_verification?.status) === "warning" ? "#f1b44c" : "#cbd5e1"}` }}
        >
          <h6 className="fw-semibold mb-3 d-flex align-items-center justify-content-between">
            <span><i className="bx bx-id-card me-1 text-primary" />Identity Verification</span>
            {report.identity_verification?.status && (
              <Badge color={reportBadgeColor(report.identity_verification.status)} pill className="fs-6">
                {report.identity_verification.status}
              </Badge>
            )}
          </h6>
          {!report.identity_verification ? (
            <p className="small text-muted mb-0">{emptySectionText}</p>
          ) : (
            <div className="small d-flex flex-column gap-1">
              {report.identity_verification.document_type && (
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Document</span>
                  <span>{report.identity_verification.document_type.type} ({report.identity_verification.document_type.state || report.identity_verification.document_type.country})</span>
                </div>
              )}
              {report.identity_verification.document_data?.full_name && (
                <div className="d-flex justify-content-between"><span className="text-muted">Name on ID</span><span>{report.identity_verification.document_data.full_name}</span></div>
              )}
              {report.identity_verification.document_data?.dob && (
                <div className="d-flex justify-content-between"><span className="text-muted">DOB on ID</span><span>{formatDate(report.identity_verification.document_data.dob)}</span></div>
              )}
              {report.identity_verification.document_data?.expiration_date && (
                <div className="d-flex justify-content-between"><span className="text-muted">ID Expires</span><span>{formatDate(report.identity_verification.document_data.expiration_date)}</span></div>
              )}
              {(report.identity_verification.consider_reasons || []).length > 0 && (
                <div className="mt-1">
                  {report.identity_verification.consider_reasons.map((reason, i) => (
                    <div key={i} className="text-warning">
                      {typeof reason === "string" ? reason : reason?.label || reason?.category || "Flagged for review"}
                    </div>
                  ))}
                </div>
              )}
              {report.identity_verification.status === "clear" && !report.identity_verification.document_type && (
                <p className="text-muted mb-0"><i className="bx bx-check-circle text-success me-1" />Identity confirmed.</p>
              )}
            </div>
          )}
        </div>
      </Col>
      <Col md={4}>
        <div
          className="rounded-3 p-3 h-100 bg-white border"
          style={{ borderLeft: `4px solid ${reportBadgeColor(report.sex_offender_registry?.status) === "success" ? "#34c38f" : reportBadgeColor(report.sex_offender_registry?.status) === "warning" ? "#f1b44c" : "#cbd5e1"}` }}
        >
          <h6 className="fw-semibold mb-3 d-flex align-items-center justify-content-between">
            <span><i className="bx bx-search-alt me-1 text-primary" />Sex Offender Registry</span>
            {report.sex_offender_registry?.status && (
              <Badge color={reportBadgeColor(report.sex_offender_registry.status)} pill className="fs-6">
                {report.sex_offender_registry.status}
              </Badge>
            )}
          </h6>
          {!report.sex_offender_registry ? (
            <p className="small text-muted mb-0">{emptySectionText}</p>
          ) : (report.sex_offender_registry.records || []).length === 0 ? (
            <p className="small text-muted mb-0"><i className="bx bx-check-circle text-success me-1" />No records found.</p>
          ) : (
            report.sex_offender_registry.records.map((r, i) => (
              <div key={i} className="small border rounded-2 p-2 mb-2" style={{ background: "#fffaf0" }}>
                <pre className="mb-0 small" style={{ whiteSpace: "pre-wrap" }}>{JSON.stringify(r, null, 1)}</pre>
              </div>
            ))
          )}
        </div>
      </Col>
      <Col md={4}>
        <div
          className="rounded-3 p-3 h-100 bg-white border"
          style={{ borderLeft: `4px solid ${reportBadgeColor(report.global_watchlist?.status) === "success" ? "#34c38f" : reportBadgeColor(report.global_watchlist?.status) === "warning" ? "#f1b44c" : "#cbd5e1"}` }}
        >
          <h6 className="fw-semibold mb-3 d-flex align-items-center justify-content-between">
            <span><i className="bx bx-globe me-1 text-primary" />Global Watchlist</span>
            {report.global_watchlist?.status && (
              <Badge color={reportBadgeColor(report.global_watchlist.status)} pill className="fs-6">
                {report.global_watchlist.status}
              </Badge>
            )}
          </h6>
          {!report.global_watchlist ? (
            <p className="small text-muted mb-0">{emptySectionText}</p>
          ) : (report.global_watchlist.records || []).length === 0 ? (
            <p className="small text-muted mb-0"><i className="bx bx-check-circle text-success me-1" />No records found.</p>
          ) : (
            report.global_watchlist.records.map((r, i) => (
              <div key={i} className="small border rounded-2 p-2 mb-2" style={{ background: "#fffaf0" }}>
                <pre className="mb-0 small" style={{ whiteSpace: "pre-wrap" }}>{JSON.stringify(r, null, 1)}</pre>
              </div>
            ))
          )}
        </div>
      </Col>
      </Row>
    </>
  );
};

export default ScreeningReportPanel;
